import { UserService } from "@services/UserService";
import { getBackendHostExternal } from "@utils/env";
import {
  CommunityResponse,
  CommunitySortType,
  CommunityView,
  PagedResponse,
  PaginationCursor,
  RequestState,
} from "lemmy-js-client";

export interface AdminListCommunities {
  sort?: CommunitySortType;
  time_range_seconds?: number;
  search_term?: string;
  search_title_only?: boolean;
  page_cursor?: PaginationCursor;
  limit?: number;
}

export interface EditSiteCommunitySettings {
  trending_multi_community_id?: number;
  suggested_multi_community_id?: number;
}

export async function adminListCommunities(
  baseUrl: string,
  form: AdminListCommunities,
  headers: Record<string, string> = {},
): Promise<RequestState<PagedResponse<CommunityView>>> {
  return get<PagedResponse<CommunityView>>(
    baseUrl,
    "/api/v4/admin/communities",
    { ...form },
    headers,
  );
}

export async function browserAdminListCommunities(
  form: AdminListCommunities,
): Promise<RequestState<PagedResponse<CommunityView>>> {
  const auth = UserService.Instance.auth();
  return adminListCommunities(
    getBackendHostExternal(),
    { ...form },
    auth ? { Authorization: `Bearer ${auth}` } : {},
  );
}

export async function editSiteCommunitySettings(
  baseUrl: string,
  form: EditSiteCommunitySettings,
  headers: Record<string, string> = {},
): Promise<RequestState<unknown>> {
  return request(
    "PUT",
    baseUrl,
    "/api/v4/site",
    undefined,
    headers,
    form,
  );
}

export async function browserEditSiteCommunitySettings(
  form: EditSiteCommunitySettings,
): Promise<RequestState<unknown>> {
  const auth = UserService.Instance.auth();
  return editSiteCommunitySettings(
    getBackendHostExternal(),
    form,
    auth ? { Authorization: `Bearer ${auth}` } : {},
  );
}

export interface CommunityCategory {
  id: number;
  name: string;
  display_order: number;
  published_at: string;
  updated_at?: string;
}

export interface ListCommunityCategoriesResponse {
  categories: CommunityCategory[];
}

export interface CommunityCategoryResponse {
  category: CommunityCategory;
}

export interface CreateCommunityCategory {
  name: string;
  display_order?: number;
}

export interface EditCommunityCategory {
  id: number;
  name?: string;
  display_order?: number;
}

export interface DeleteCommunityCategory {
  id: number;
}

export async function listCommunityCategories(
  baseUrl: string,
  headers: Record<string, string> = {},
  params: { with_communities_only?: boolean } = {},
): Promise<RequestState<ListCommunityCategoriesResponse>> {
  return get<ListCommunityCategoriesResponse>(
    baseUrl,
    "/api/v4/community/category/list",
    params,
    headers,
  );
}

export async function browserListCommunityCategories(
  params: { with_communities_only?: boolean } = {},
): Promise<RequestState<ListCommunityCategoriesResponse>> {
  return listCommunityCategories(getBackendHostExternal(), {}, params);
}

export async function createCommunityCategory(
  baseUrl: string,
  form: CreateCommunityCategory,
  headers: Record<string, string> = {},
): Promise<RequestState<CommunityCategoryResponse>> {
  return request(
    "POST",
    baseUrl,
    "/api/v4/admin/community-category",
    undefined,
    headers,
    form,
  );
}

export async function browserCreateCommunityCategory(
  form: CreateCommunityCategory,
): Promise<RequestState<CommunityCategoryResponse>> {
  const auth = UserService.Instance.auth();
  return createCommunityCategory(
    getBackendHostExternal(),
    form,
    auth ? { Authorization: `Bearer ${auth}` } : {},
  );
}

export async function editCommunityCategory(
  baseUrl: string,
  form: EditCommunityCategory,
  headers: Record<string, string> = {},
): Promise<RequestState<CommunityCategoryResponse>> {
  return request(
    "PUT",
    baseUrl,
    "/api/v4/admin/community-category",
    undefined,
    headers,
    form,
  );
}

export async function browserEditCommunityCategory(
  form: EditCommunityCategory,
): Promise<RequestState<CommunityCategoryResponse>> {
  const auth = UserService.Instance.auth();
  return editCommunityCategory(
    getBackendHostExternal(),
    form,
    auth ? { Authorization: `Bearer ${auth}` } : {},
  );
}

export async function deleteCommunityCategory(
  baseUrl: string,
  form: DeleteCommunityCategory,
  headers: Record<string, string> = {},
): Promise<RequestState<{ success: boolean }>> {
  return request(
    "DELETE",
    baseUrl,
    "/api/v4/admin/community-category",
    undefined,
    headers,
    form,
  );
}

export async function browserDeleteCommunityCategory(
  form: DeleteCommunityCategory,
): Promise<RequestState<{ success: boolean }>> {
  const auth = UserService.Instance.auth();
  return deleteCommunityCategory(
    getBackendHostExternal(),
    form,
    auth ? { Authorization: `Bearer ${auth}` } : {},
  );
}

export interface CreateCommunityForm {
  name: string;
  title?: string;
  sidebar?: string;
  summary?: string;
  nsfw?: boolean;
  posting_restricted_to_mods?: boolean;
  visibility?: string;
  category_id: number;
}

export async function createCommunity(
  baseUrl: string,
  form: CreateCommunityForm,
  headers: Record<string, string> = {},
): Promise<RequestState<CommunityResponse>> {
  return request(
    "POST",
    baseUrl,
    "/api/v4/community",
    undefined,
    headers,
    form,
  );
}

export async function browserCreateCommunity(
  form: CreateCommunityForm,
): Promise<RequestState<CommunityResponse>> {
  const auth = UserService.Instance.auth();
  return createCommunity(
    getBackendHostExternal(),
    form,
    auth ? { Authorization: `Bearer ${auth}` } : {},
  );
}

export interface EditCommunityForm {
  community_id: number;
  title?: string;
  sidebar?: string;
  summary?: string;
  nsfw?: boolean;
  posting_restricted_to_mods?: boolean;
  discussion_languages?: number[];
  visibility?: string;
  hot_score_admin?: number;
  suggested_score?: number;
  /** Pass `null` to clear category. Omit to leave unchanged. */
  category_id?: number | null;
}

export async function editCommunityApi(
  baseUrl: string,
  form: EditCommunityForm,
  headers: Record<string, string> = {},
): Promise<RequestState<CommunityResponse>> {
  return request(
    "PUT",
    baseUrl,
    "/api/v4/community",
    undefined,
    headers,
    form,
  );
}

export async function browserEditCommunity(
  form: EditCommunityForm,
): Promise<RequestState<CommunityResponse>> {
  const auth = UserService.Instance.auth();
  return editCommunityApi(
    getBackendHostExternal(),
    form,
    auth ? { Authorization: `Bearer ${auth}` } : {},
  );
}

async function get<T>(
  baseUrl: string,
  path: string,
  params: Record<string, unknown>,
  headers: Record<string, string>,
): Promise<RequestState<T>> {
  return request<T>("GET", baseUrl, path, params, headers);
}

async function request<T>(
  method: "GET" | "PUT" | "POST" | "DELETE",
  baseUrl: string,
  path: string,
  params?: Record<string, unknown>,
  headers: Record<string, string> = {},
  body?: unknown,
): Promise<RequestState<T>> {
  try {
    const searchParams = new URLSearchParams();
    Object.entries(params ?? {})
      .filter(([, value]) => value !== undefined && value !== null)
      .forEach(([key, value]) => searchParams.set(key, String(value)));

    const query = searchParams.toString();
    const response = await fetch(
      `${baseUrl.replace(/\/+$/, "")}${path}${query ? `?${query}` : ""}`,
      {
        method,
        headers: body
          ? {
              "Content-Type": "application/json",
              ...headers,
            }
          : headers,
        body: body ? JSON.stringify(body) : undefined,
      },
    );
    const json = await response.json();

    if (!response.ok) {
      return {
        state: "failed",
        err: new Error(json?.message ?? json?.error ?? response.statusText),
      };
    }

    return { state: "success", data: json as T };
  } catch (err) {
    return {
      state: "failed",
      err: err instanceof Error ? err : new Error(String(err)),
    };
  }
}
